import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { briefingService, Briefing } from '../../services/briefingService';
import { BriefingForm } from '../../components/BriefingForm';

export function PortalBriefings() {
  const [briefings, setBriefings] = useState<Briefing[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Briefing | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBriefings();
  }, []);

  const loadBriefings = async () => {
    try {
      setLoading(true);
      const data = await briefingService.list({ status: 'PENDENTE' });
      setBriefings(data);
    } catch (e) {
      console.error('Erro ao carregar briefings:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (dados: Record<string, any>) => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await briefingService.responder(selected.id, dados);
      setDone(true);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao enviar o briefing');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSave = async (dados: Record<string, any>) => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const b = await briefingService.salvar(selected.id, dados);
      setSelected(b);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao salvar o rascunho');
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="space-y-6">
        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-4">Briefing enviado!</h1>
          <p className="text-muted-foreground mb-6">
            Obrigado por preencher o briefing. Nossa equipe comercial analisará as informações e entrará em contato.
          </p>
          <button
            onClick={() => { setDone(false); setSelected(null); loadBriefings(); }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => { setSelected(null); setError(null); }}
            className="p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Briefing de Novo Projeto</h1>
            <p className="text-muted-foreground">Preencha os campos abaixo</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-8">
          {selected.mensagemRevisao && (
            <div className="mb-6 bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-1">
                Solicitação de revisão da nossa equipe
              </h3>
              <p className="text-sm text-foreground whitespace-pre-wrap">{selected.mensagemRevisao}</p>
            </div>
          )}
          <BriefingForm
            tipo={(selected.tipo as any) || 'SAAS_PRAGAS'}
            onSubmit={handleSubmit}
            onSave={handleSave}
            submitting={submitting}
            saving={saving}
            initialValues={selected.dados || undefined}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Meus Briefings</h1>
          <p className="text-muted-foreground">
            Briefings de novos projetos pendentes de resposta
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : briefings.length === 0 ? (
        <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum briefing pendente</h3>
          <p className="text-muted-foreground">
            Você não possui briefings aguardando resposta no momento.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {briefings.map((b) => (
            <div
              key={b.id}
              className="flex items-center justify-between bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6"
            >
              <div>
                <h3 className="text-lg font-medium text-foreground">
                  {b.cliente?.nomeFantasia || b.cliente?.razaoSocial || 'Briefing de Novo Projeto'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Solicitado em {new Date(b.createdAt).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <button
                onClick={() => setSelected(b)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Preencher
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
